/**
 * CR331 + CR403 + CR431: Reward Merchant Detection Table for S6.5 Diagnostics
 * Shows detected reward brands per batch run with bucket-based classification.
 * CR431: Now reads from v_truth_table_s12 (LEFT JOIN) so ALL brands from staging
 * always appear, even before S6.5 has matched them.
 */
import { useState, useEffect, useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, Search, CheckCircle2, AlertCircle, Loader2, Eye, EyeOff, Bot, Trash2, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

// CR431: Extended bucket types — PENDING_MATCH for brands not yet processed by S6.5
type Bucket = "FOUND_EXISTING" | "PENDING_NEW" | "MATCHED_AUTO" | "MATCHED_HIDDEN" | "SUPPRESSED_DIRTY" | "PENDING_MATCH";

interface S65Row {
  batch_run_id: string;
  candidate_row_id: string;
  candidate_name: string;
  candidate_slug: string;
  normalized_candidate_name: string | null;
  normalized_candidate_slug: string | null;
  detected_in_field: string;
  match_type: string | null;
  match_confidence: number | null;
  candidate_status: string;
  is_dirty: boolean;
  dirty_reasons: string[];
  merchant_id: string | null;
  matched_merchant_key: string | null;
  matched_display_name: string | null;
  matched_is_hidden: boolean | null;
  matched_merchant_source: string | null;
  bucket: Bucket;
  occurrences: number;
}

interface BucketConfig {
  label: string;
  icon: React.ReactNode;
  badgeClass: string;
  description: string;
}

const BUCKET_CONFIG: Record<Bucket, BucketConfig> = {
  FOUND_EXISTING: {
    label: "Found Existing",
    icon: <CheckCircle2 className="h-3.5 w-3.5 text-primary" />,
    badgeClass: "bg-primary/10 text-primary border-primary/20",
    description: "Matched to an active merchant",
  },
  PENDING_NEW: {
    label: "Pending New",
    icon: <AlertCircle className="h-3.5 w-3.5 text-destructive" />,
    badgeClass: "bg-destructive/10 text-destructive border-destructive/20",
    description: "No merchant match found — needs triage",
  },
  MATCHED_AUTO: {
    label: "Matched Auto",
    icon: <Bot className="h-3.5 w-3.5 text-accent-foreground" />,
    badgeClass: "bg-accent text-accent-foreground border-accent",
    description: "Matched to an auto-created merchant",
  },
  MATCHED_HIDDEN: {
    label: "Matched Hidden",
    icon: <EyeOff className="h-3.5 w-3.5 text-muted-foreground" />,
    badgeClass: "bg-muted text-muted-foreground border-muted-foreground/20",
    description: "Matched to a hidden merchant",
  },
  SUPPRESSED_DIRTY: {
    label: "Suppressed",
    icon: <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />,
    badgeClass: "bg-muted text-muted-foreground border-muted-foreground/20",
    description: "Dirty candidate — suppressed from matching",
  },
  PENDING_MATCH: {
    label: "Awaiting S6.5",
    icon: <Clock className="h-3.5 w-3.5 text-muted-foreground" />,
    badgeClass: "bg-muted/50 text-muted-foreground border-muted-foreground/20",
    description: "Extracted from flyer but not yet processed by S6.5",
  },
};

const BUCKET_ORDER: Bucket[] = ["FOUND_EXISTING", "PENDING_NEW", "MATCHED_AUTO", "MATCHED_HIDDEN", "SUPPRESSED_DIRTY", "PENDING_MATCH"];

interface S65RewardMerchantsTableProps {
  batchRunId: string;
}

type FilterMode = "all" | Bucket;

export function S65RewardMerchantsTable({ batchRunId }: S65RewardMerchantsTableProps) {
  const [rows, setRows] = useState<S65Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterMode, setFilterMode] = useState<FilterMode>("all");

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);
      
      // CR431: Primary source is v_truth_table_s12 (LEFT JOIN enrichment)
      const { data, error: err } = await supabase
        .from("v_truth_table_s12" as any)
        .select("*")
        .eq("batch_run_id", batchRunId);

      if (err) {
        // Fallback to v_s65_merchant_scan_results if v_truth_table_s12 not available
        console.warn("[S6.5 UI] v_truth_table_s12 failed, trying v_s65_merchant_scan_results:", err.message);
        const { data: fallbackData, error: fallbackErr } = await supabase
          .from("v_s65_merchant_scan_results" as any)
          .select("*")
          .eq("batch_run_id", batchRunId);

        if (fallbackErr) {
          setError(fallbackErr.message);
          setRows([]);
        } else {
          setRows((fallbackData as unknown as S65Row[]) || []);
        }
      } else {
        // CR431: Map v_truth_table_s12 rows to S65Row format
        // Brands without S6.5 match get bucket "PENDING_MATCH"
        const mapped = (data || []).map((r: any) => {
          const hasMatch = r.candidate_status != null || r.match_type != null;
          const bucket: Bucket = hasMatch
            ? (r.is_dirty ? "SUPPRESSED_DIRTY"
              : r.merchant_id == null ? "PENDING_NEW"
              : r.matched_is_hidden ? "MATCHED_HIDDEN"
              : r.matched_merchant_key && r.match_type === 'auto' ? "MATCHED_AUTO"
              : "FOUND_EXISTING")
            : "PENDING_MATCH";
          
          return {
            batch_run_id: r.batch_run_id,
            candidate_row_id: `${r.batch_run_id}_${r.candidate_slug}`,
            candidate_name: r.candidate_name,
            candidate_slug: r.candidate_slug,
            normalized_candidate_name: null,
            normalized_candidate_slug: r.candidate_slug,
            detected_in_field: Array.isArray(r.detected_in_fields) ? r.detected_in_fields.join(', ') : '',
            match_type: r.match_type,
            match_confidence: r.match_confidence,
            candidate_status: r.candidate_status || (hasMatch ? 'matched' : 'pending'),
            is_dirty: r.is_dirty || false,
            dirty_reasons: r.dirty_reasons || [],
            merchant_id: r.merchant_id,
            matched_merchant_key: r.matched_merchant_key,
            matched_display_name: r.matched_display_name,
            matched_is_hidden: r.matched_is_hidden,
            matched_merchant_source: null,
            bucket,
            occurrences: r.occurrences || 1,
          } as S65Row;
        });
        setRows(mapped);
      }
      setLoading(false);
    }
    fetchData();
  }, [batchRunId]);

  // Bucket counts
  const bucketCounts = useMemo(() => {
    const counts: Record<Bucket, number> = {
      FOUND_EXISTING: 0,
      PENDING_NEW: 0,
      MATCHED_AUTO: 0,
      MATCHED_HIDDEN: 0,
      SUPPRESSED_DIRTY: 0,
      PENDING_MATCH: 0,
    };
    for (const r of rows) {
      const b = r.bucket as Bucket;
      if (b in counts) counts[b]++;
    }
    return counts;
  }, [rows]);

  const uniqueBrands = useMemo(() => {
    return new Set(rows.map(r => r.normalized_candidate_slug || r.candidate_slug)).size;
  }, [rows]);

  const filteredRows = useMemo(() => {
    let filtered = rows;

    if (filterMode !== "all") {
      filtered = filtered.filter(r => r.bucket === filterMode);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.candidate_name.toLowerCase().includes(q) ||
        r.candidate_slug.toLowerCase().includes(q) ||
        (r.normalized_candidate_name?.toLowerCase().includes(q)) ||
        (r.matched_merchant_key?.toLowerCase().includes(q)) ||
        (r.matched_display_name?.toLowerCase().includes(q))
      );
    }

    return filtered;
  }, [rows, filterMode, searchQuery]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
        <Loader2 className="h-3 w-3 animate-spin" />
        Loading reward brand data…
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-sm text-destructive py-2">
        Failed to load reward brands: {error}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-2">
        No reward brands detected for this batch.
      </p>
    );
  }

  // Build filter buttons for non-zero buckets
  const activeBuckets = BUCKET_ORDER.filter(b => bucketCounts[b] > 0);

  return (
    <div className="space-y-3">
      {/* Summary pills — always visible */}
      <div className="flex items-center gap-3 flex-wrap text-sm">
        <span className="flex items-center gap-1.5">
          <Eye className="h-3.5 w-3.5 text-muted-foreground" />
          <strong>{uniqueBrands}</strong> unique brands
        </span>
        {BUCKET_ORDER.map(bucket => {
          const count = bucketCounts[bucket];
          if (count === 0) return null;
          const config = BUCKET_CONFIG[bucket];
          return (
            <span key={bucket} className="flex items-center gap-1.5">
              <span className="text-muted-foreground">·</span>
              {config.icon}
              <strong>{count}</strong> {config.label.toLowerCase()}
            </span>
          );
        })}
        <span className="text-muted-foreground">·</span>
        <span className="text-muted-foreground">{rows.length} total rows</span>
      </div>

      {/* Collapsible table */}
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-1.5 h-7 px-2 text-xs">
            <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? "rotate-180" : ""}`} />
            {isOpen ? "Hide" : "Show"} detected brands
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-2 space-y-2">
            {/* Filters */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[200px] max-w-xs">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  placeholder="Search brands…"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="h-7 pl-7 text-xs"
                />
              </div>
              <div className="flex gap-1 flex-wrap">
                <Button
                  variant={filterMode === "all" ? "default" : "outline"}
                  size="sm"
                  className="h-7 text-xs px-2.5"
                  onClick={() => setFilterMode("all")}
                >
                  All ({rows.length})
                </Button>
                {activeBuckets.map(bucket => (
                  <Button
                    key={bucket}
                    variant={filterMode === bucket ? "default" : "outline"}
                    size="sm"
                    className="h-7 text-xs px-2.5"
                    onClick={() => setFilterMode(bucket)}
                  >
                    {BUCKET_CONFIG[bucket].label} ({bucketCounts[bucket]})
                  </Button>
                ))}
              </div>
            </div>

            {/* Table */}
            <div className="border rounded-md max-h-[400px] overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Brand</TableHead>
                    <TableHead className="text-xs">Normalized</TableHead>
                    <TableHead className="text-xs">Bucket</TableHead>
                    <TableHead className="text-xs">Mapped To</TableHead>
                    <TableHead className="text-xs">Detected In</TableHead>
                    <TableHead className="text-xs">Match Type</TableHead>
                    <TableHead className="text-xs text-right">#</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground text-xs py-4">
                        No brands match the current filter.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRows.map(row => {
                      const config = BUCKET_CONFIG[row.bucket] || BUCKET_CONFIG.PENDING_NEW;
                      return (
                        <TableRow key={row.candidate_row_id} className={row.is_dirty ? "opacity-50" : ""}>
                          <TableCell className="text-xs font-medium">
                            {row.candidate_name}
                            {row.is_dirty && row.dirty_reasons.length > 0 && (
                              <span className="ml-1 text-muted-foreground" title={row.dirty_reasons.join(", ")}>
                                ⚠
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs font-mono text-muted-foreground">
                            {row.normalized_candidate_slug || row.candidate_slug}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`text-xs ${config.badgeClass}`}>
                              {config.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs">
                            {row.matched_display_name || row.matched_merchant_key || (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs font-mono text-muted-foreground">
                            {row.detected_in_field}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {row.match_type}
                          </TableCell>
                          <TableCell className="text-xs text-right tabular-nums">
                            {row.occurrences}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
